import { Stack } from '@mui/system';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { SvgColor } from '../svg-color';
import { Scrollbar } from '../scrollbar';
import { Strings } from '@app/constants';

interface Props {
  title?: string;
  fields?: string[];
  data?: Array<string | number | undefined>;
  height?: string | number;
  width?: any;
  iconImg?: any;
  ratingData?: string[];
  ratingIconImg?: any;
}

const DetailsCard = ({
  title,
  fields,
  data,
  height,
  width,
  iconImg,
  ratingData,
  ratingIconImg,
}: Props) => {
  const theme = useTheme();
  return (
    <Card sx={{ margin: 1, height, width }}>
      {title && (
        <Stack
          sx={{
            borderRadius: '0px 0px 10px 10px',
            bgcolor: theme.palette.info.lighter,
            ml: '60px',
            mr: '60px',
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={13}
            sx={{
              padding: '5px',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </Stack>
      )}
      <Scrollbar sx={{ height: '230px' }}>
        {iconImg && (
          <Grid item xs={12} md={12}>
            <CardMedia sx={{ display: 'flex', justifyContent: 'center', direction: 'row' }}>
              <Stack
                sx={{
                  width: 100,
                  height: 100,
                  mt: '20px',
                  border: '2px dashed #D8DBDE',
                  borderStyle: 'dashed',
                  borderRadius: '50%',
                }}
              >
                <Stack
                  sx={{
                    bgcolor: theme.palette.info.main,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    alignSelf: 'center',
                    mt: '10px',
                    mb: '10px',
                  }}
                >
                  <SvgColor
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    src={`/assets/icons/card/${iconImg}.svg`}
                    sx={{
                      width: '50px',
                      height: '50px',
                      alignSelf: 'center',
                      color: theme.palette.common.white,
                      mt: '12px',
                    }}
                  />
                </Stack>
              </Stack>
            </CardMedia>
          </Grid>
        )}
        {ratingData && (
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Grid item xs={12} md={12}>
              <CardContent>
                <Table>
                  <TableBody>
                    {ratingData.map((field: any, index: any) => (
                      <TableRow key={index}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            borderBottom: 'none',
                            verticalAlign: 'top',
                            alignContent: 'center',
                            pr: '2px',
                            pl: '0px',
                          }}
                        >
                          {field}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Grid>
            <Grid item xs={12} md={12}>
              <CardMedia sx={{ display: 'flex', justifyContent: 'center', direction: 'row' }}>
                <Stack
                  sx={{
                    width: 40,
                    height: 40,
                    border: '2px dashed #D8DBDE',
                    borderStyle: 'dashed',
                    borderRadius: '50%',
                    justifyContent: 'center',
                  }}
                >
                  <Stack
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Stack
                      sx={{
                        bgcolor: theme.palette.info.main,
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        alignSelf: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SvgColor
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        src={`/assets/icons/card/${ratingIconImg}.svg`}
                        sx={{
                          width: '20px',
                          height: '20px',
                          color: theme.palette.common.white,
                          alignSelf: 'center',
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </CardMedia>
            </Grid>
          </Stack>
        )}
        <Grid item xs={12} md={12}>
          <CardContent sx={{ paddingTop: '10px' }}>
            <Table size="small">
              <TableBody>
                {data && data.length > 0 ? (
                  fields?.map((field, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          borderBottom: 'none',
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap',
                          pr: '8px',
                          pl: '0px',
                        }}
                      >
                        {field}:
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: 'none',
                          pr: '0px',
                          pl: '0px',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {data[index]}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <Box display="flex" justifyContent="center">
                    <Typography variant="body2" color="textSecondary">
                      {Strings.common.noRecord}
                    </Typography>
                  </Box>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Grid>
      </Scrollbar>
    </Card>
  );
};

export { DetailsCard };
